import { Contact } from '@/types/contact';
import { TableCell, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { MoreHorizontal, User, Mail, Building2, Edit, Trash2, Eye, UserPlus } from 'lucide-react';
import { format } from 'date-fns';
import { Badge } from '@/components/ui/badge';
import { useNavigate } from 'react-router-dom';
import { Checkbox } from '@/components/ui/checkbox';
import { useAppDispatch } from '@/hooks/useAppDispatch';
import { toggleContactSelection, deleteContact, updateCustomer } from '@/store/slices/contactSlice';
import { ContactActivityBadge } from './ContactActivityBadge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import SimpleEllipsis from '../ui/ellipsis';
import { toast } from 'sonner';

interface ContactListItemProps {
  contact: Contact;
  isSelected: boolean;
  index?: number;
}

export const ContactListItem = ({ contact, isSelected, index = 0 }: ContactListItemProps) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const handleRowClick = () => {
    navigate(`/home/contacts/${contact.id}`);
  };

  const handleCheckboxClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    dispatch(toggleContactSelection(contact.id));
  };

  const handleViewContact = () => {
    navigate(`/home/contacts/${contact.id}`);
  };

  const handleEditContact = () => {
    // This would open an edit dialog or navigate to edit page
    console.log('Edit contact:', contact.id);
  };

  const handleDeleteContact = async () => {
    try {
      await dispatch(deleteContact(contact.id)).unwrap();
      toast.success('Contact has been deleted.');
    } catch (error) {
      console.error('Error deleting contact:', error);
      toast.error('Failed to delete contact.');
    }
  };

  const handleAddToCustomer = async () => {
    try {
      await dispatch(updateCustomer({ customer_id: contact.id, type: 'customer' })).unwrap();
      toast.success('Contact has been upgraded to customer.');
    } catch (error) {
      console.error('Error upgrading contact to customer:', error);
      toast.error('Failed to upgrade contact to customer.');
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'customer': return 'bg-emerald-100 text-emerald-800 border-emerald-200';
      case 'lead': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'visitor': return 'bg-amber-100 text-amber-800 border-amber-200';
      default: return 'bg-slate-100 text-slate-800 border-slate-200';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800 border-green-200';
      case 'inactive': return 'bg-red-100 text-red-800 border-red-200';
      default: return 'bg-slate-100 text-slate-800 border-slate-200';
    }
  };

  const getInitials = (firstname: string, lastname: string) => {
    return `${firstname.charAt(0)}${lastname.charAt(0)}`.toUpperCase();
  };

  return (
    <TableRow
      className={`
        cursor-pointer transition-all duration-200 border-b border-slate-100/60
        hover:bg-gradient-to-r hover:from-blue-50/30 hover:via-indigo-50/20 hover:to-purple-50/30
        hover:shadow-sm hover:border-blue-200/40
        ${isSelected ? 'bg-gradient-to-r from-blue-50/60 via-indigo-50/40 to-purple-50/60 border-blue-200/60' : ''}
        ${index % 2 === 0 ? 'bg-slate-50/20' : 'bg-white'}
      `}
      onClick={handleRowClick}
      style={{ animationDelay: `${index * 50}ms` }}
    >
      <TableCell onClick={handleCheckboxClick} className="py-4">
        <Checkbox
          checked={isSelected}
          className={`
            data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600
            transition-all duration-200
            ${isSelected ? 'shadow-sm shadow-blue-500/25' : ''}
          `}
        />
      </TableCell>

      <TableCell className="py-4">
        <div className="flex items-center gap-3">
          <Avatar className="h-8 w-8 ring-2 ring-white shadow-sm">
            <AvatarImage src={''} alt={`${contact.firstname} ${contact.lastname}`} />
            <AvatarFallback className="bg-gradient-to-br from-blue-500 to-indigo-600 text-white text-xs font-medium">
              {getInitials(contact.firstname, contact.lastname)}
            </AvatarFallback>
          </Avatar>
          <div className="flex flex-col">
            <span className="font-medium text-slate-900 hover:text-blue-600 transition-colors">
              <SimpleEllipsis maxLength={10} tooltip={true}>
                {[contact.firstname, contact.lastname]
                  .filter(name => name && name.trim())
                  .join(' ')
                  .replace(/,\s*,/g, ',') // Remove redundant commas
                  .replace(/^,|,$/g, '') // Remove leading/trailing commas
                }
              </SimpleEllipsis>
            </span>
            {contact.phone && (
              <span className="text-xs text-slate-500 flex items-center gap-1">
                <div className="w-1 h-1 bg-slate-300 rounded-full"></div>
                <SimpleEllipsis maxLength={10} tooltip={true}>
                  {contact.phone}
                </SimpleEllipsis>
              </span>
            )}
          </div>
        </div>
      </TableCell>

      <TableCell className="py-4">
        <div className="flex items-center gap-2">
          <Mail className="h-3 w-3 text-slate-400" />
          <span className="text-slate-700 hover:text-blue-600 transition-colors cursor-pointer">
            <SimpleEllipsis maxLength={10} tooltip={true}>
              {contact.email}
            </SimpleEllipsis>
          </span>
        </div>
      </TableCell>

      <TableCell className="py-4">
        {contact.company ? (
          <div className="flex items-center gap-2">
            <Building2 className="h-3 w-3 text-slate-400" />
            <span className="text-slate-700">
              <SimpleEllipsis maxLength={10} tooltip={true}>
                {contact.company}
              </SimpleEllipsis>
            </span>
          </div>
        ) : (
          <span className="text-slate-400 italic">No company</span>
        )}
      </TableCell>

      <TableCell className="py-4">
        <Badge
          variant="outline"
          className={`${getTypeColor(contact.type)} font-medium border transition-all duration-200 hover:shadow-sm`}
        >
          {contact.type}
        </Badge>
      </TableCell>

      <TableCell className="py-4">
        <Badge
          variant="outline"
          className={`${getStatusColor(contact.status)} font-medium border transition-all duration-200 hover:shadow-sm`}
        >
          {contact.status}
        </Badge>
      </TableCell>

      <TableCell className="py-4">
        <div className={`text-slate-600 ${!contact.lastContacted ? 'italic text-slate-400' : ''}`}>
          <SimpleEllipsis maxLength={10} tooltip={true}>
            {contact.lastContacted ? format(new Date(contact.lastContacted), 'MMM dd, yyyy') : 'Never'}
          </SimpleEllipsis>
        </div>
      </TableCell>

      <TableCell className="py-4">
        <ContactActivityBadge contact={contact} />
      </TableCell>

      {/* Actions dropdown menu */}
      <TableCell className="text-right py-4" onClick={(e) => e.stopPropagation()}>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 hover:bg-slate-100 hover:text-slate-900 transition-all duration-200 rounded-lg"
            >
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuLabel>Actions</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleViewContact} className="cursor-pointer">
              <Eye className="mr-2 h-4 w-4" />
              View Details
            </DropdownMenuItem>
            {/* <DropdownMenuItem onClick={handleEditContact} className="cursor-pointer">
              <Edit className="mr-2 h-4 w-4" />
              Edit Contact
            </DropdownMenuItem> */}
            {contact.type !== 'customer' && (
              <DropdownMenuItem onClick={handleAddToCustomer} className="cursor-pointer">
                <UserPlus className="mr-2 h-4 w-4" />
                Add to Customers
              </DropdownMenuItem>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleDeleteContact} className="cursor-pointer text-red-600 hover:text-red-700">
              <Trash2 className="mr-2 h-4 w-4" />
              Delete Contact
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </TableCell>
    </TableRow>
  );
};
